import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import firebaseApp, { db } from "services/firebase";

const CREATE_BOOKING = "createBooking";
const PREVIEW_COVERAGE = "previewCoverageConfidence";
const CREATE_AVAILABILITY = "createAvailability";
const UPDATE_AVAILABILITY = "updateAvailability";
const DELETE_AVAILABILITY = "deleteAvailability";
const COPY_DEMOGRAPHICS = "copyDemographicsToBookingPhi";

const getCallable = (name) => firebaseApp.functions().httpsCallable(name);

/**
 * Create a CareVerify Tier 2 booking (capacity-gated + coverage-gated).
 * @param {object} payload
 * @returns {Promise<object>}
 */
export async function createCareVerifyBooking(payload) {
  try {
    const { data } = await getCallable(CREATE_BOOKING)(payload);
    return data;
  } catch (err) {
    throw new Error(
      err?.message || "Could not complete booking. Please try again.",
    );
  }
}

/**
 * Preview coverage confidence for the Confirm step (PROMPT 5).
 * @param {object} payload
 * @returns {Promise<{ coverageConfidenceTier: string }>}
 */
export async function previewCareVerifyCoverage(payload) {
  try {
    const { data } = await getCallable(PREVIEW_COVERAGE)(payload);
    return data;
  } catch (err) {
    throw new Error(err?.message || "Could not preview coverage confidence.");
  }
}

/**
 * Copy MasterChart demographics onto an existing bookingPhi (PROMPT 7).
 */
export async function copyDemographicsToBookingPhi(payload) {
  try {
    const { data } = await getCallable(COPY_DEMOGRAPHICS)(payload);
    return data;
  } catch (err) {
    throw new Error(err?.message || "Could not copy demographics.");
  }
}

export async function createAvailability(payload) {
  try {
    const { data } = await getCallable(CREATE_AVAILABILITY)(payload);
    return data;
  } catch (err) {
    throw new Error(err?.message || "Could not create availability.");
  }
}

export async function updateAvailability(payload) {
  try {
    const { data } = await getCallable(UPDATE_AVAILABILITY)(payload);
    return data;
  } catch (err) {
    throw new Error(err?.message || "Could not update availability.");
  }
}

export async function deleteAvailability(payload) {
  try {
    const { data } = await getCallable(DELETE_AVAILABILITY)(payload);
    return data;
  } catch (err) {
    throw new Error(err?.message || "Could not delete availability.");
  }
}

/**
 * Public-read availability catalog for a facility.
 * @param {string} facilityId
 * @returns {Promise<Array<object>>}
 */
export async function listFacilityAvailability(facilityId) {
  if (!facilityId) return [];
  const snap = await db
    .collection("locations")
    .doc(facilityId)
    .collection("availability")
    .get();
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

/**
 * Subscribe to facility availability (facility dashboard).
 * @param {string} facilityId
 * @param {(rows: object[]) => void} onData
 * @param {(err: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function subscribeFacilityAvailability(facilityId, onData, onError) {
  if (!facilityId) return () => {};
  return db
    .collection("locations")
    .doc(facilityId)
    .collection("availability")
    .onSnapshot(
      (snap) => {
        onData(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      },
      (err) => {
        if (onError) onError(err);
      },
    );
}

/**
 * Load a small public facility list for CareVerify v1 Autocomplete.
 * Prefers seeded CareVerify demos; also merges any `status === 'approved'`
 * docs the client can see (locations are public-read).
 */
export async function listCareVerifyFacilities() {
  const byId = new Map();

  const seedIds = [
    "careverify-demo-riverfront",
    "careverify-demo-oakstreet",
  ];
  await Promise.all(
    seedIds.map(async (id) => {
      try {
        const snap = await db.collection("locations").doc(id).get();
        if (snap.exists) {
          byId.set(id, { id, ...snap.data() });
        }
      } catch {
        /* ignore missing seed */
      }
    }),
  );

  try {
    const snap = await db
      .collection("locations")
      .where("status", "==", "approved")
      .limit(40)
      .get();
    snap.docs.forEach((doc) => {
      if (!byId.has(doc.id)) {
        byId.set(doc.id, { id: doc.id, ...doc.data() });
      }
    });
  } catch {
    /* index may be missing in some envs — seed list is enough for v1 */
  }

  return Array.from(byId.values()).map((loc) => ({
    ...loc,
    displayName: loc.title || loc.name || loc.id,
  }));
}

// silence unused firebase import lint in some configs
void firebase;
